import { styled } from '@linaria/react';
import {
  bodyFont,
  fonts,
  layout,
  primitives,
  semantic,
  shadow,
  size,
  space,
  textStyle,
} from '../../theme/tokens';
import { defaultCardAspectRatio, EntityImage } from '../EntityImage';
import { Badge } from '../Badge';
import { TextButton } from '../TextButton';

export const ItemCardBase = styled.div<{ $unavailable?: boolean; $clickable: boolean }>`
  display: flex;
  flex-direction: column;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  opacity: ${({ $unavailable }) => ($unavailable ? 0.55 : 1)};
  transition: transform 0.2s ease;

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
    border-radius: ${size.radius['radius-lg']};
  }
`;

export const ImageWrapper = styled.div<{
  $aspectRatio?: string;
  $mobileAspectRatio?: string;
  $imageOverlay?: boolean;
  $overlayScrim?: boolean;
  $size: 'small' | 'large';
}>`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ $mobileAspectRatio, $aspectRatio, $size }) =>
    $mobileAspectRatio ?? $aspectRatio ?? defaultCardAspectRatio($size).mobile};
  overflow: hidden;
  border-radius: ${({ $size }) =>
    $size === 'small' ? size.radius['radius-sm'] : size.radius['radius-lg']};
  background: ${semantic.bg.surfaceSubtle};
  isolation: isolate;

  @media (min-width: ${layout.desktopBreakpoint}) {
    aspect-ratio: ${({ $aspectRatio, $size }) =>
      $aspectRatio ?? defaultCardAspectRatio($size).desktop};
  }

  &::before {
    content: ${({ $overlayScrim }) => ($overlayScrim ? "''" : 'none')};
    position: absolute;
    inset: 0;
    z-index: 1;
    background: ${semantic.bg.overlayImage};
    pointer-events: none;
  }

  &::after {
    content: ${({ $imageOverlay }) => ($imageOverlay ? "''" : 'none')};
    position: absolute;
    inset: 0;
    background: ${semantic.bg.surfaceSubtle};
    mix-blend-mode: multiply;
    pointer-events: none;
  }
`;

export const Image = styled(EntityImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const Placeholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${semantic.text.tertiary};
  font-family: ${bodyFont};
  font-size: ${fonts.size.xs};
`;

export const ImageBadge = styled(Badge)`
  position: absolute;
  top: ${space['space-600']};
  left: ${space['space-600']};
`;

export const ImageAction = styled.button`
  position: absolute;
  z-index: 1;
  top: ${space['space-300']};
  right: ${space['space-300']};
  display: inline-flex;
  align-items: center;
  gap: ${space['space-100']};
  font-family: ${fonts.body};
  font-size: ${fonts.size.xs};
  font-weight: ${fonts.weight.medium};
  color: ${semantic.text.inverse};
  background: ${primitives.color.black['color-black-alpha-600']};
  border: none;
  border-radius: ${size.radius['radius-full']};
  padding: ${space['space-100']} ${space['space-300']};
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${primitives.color.black['color-black-alpha-700']};
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.text.inverse};
    outline-offset: ${space['space-050']};
  }
`;

export const Content = styled.div<{
  $textPosition: 'left' | 'center' | 'right';
  $size: 'small' | 'large';
}>`
  display: flex;
  flex-direction: column;
  gap: ${({ $size }) => ($size === 'small' ? space['space-300'] : space['space-400'])};
  padding: ${space['space-500']} 0 ${space['space-200']};
  text-align: ${({ $textPosition }) => $textPosition};
`;

export const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-050']};
`;

export const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const ContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${space['space-400']};
`;

export const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-400']};
  flex: 1;
  min-width: 0;
`;

export const TitleMetaGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-100']};
`;

export const Title = styled.h3<{ $bold?: boolean; $size: 'small' | 'large' }>`
  font-family: ${bodyFont};
  font-size: ${({ $size }) =>
    $size === 'small' ? textStyle.body.md.strong.fontSize : fonts.size.base};
  font-weight: ${({ $bold, $size }) => {
    if ($size === 'small') return fonts.weight.medium;
    return $bold ? fonts.weight.semiBold : fonts.weight.normal;
  }};
  color: ${semantic.text.primary};
  margin: 0;
  line-height: ${primitives.typography.lineHeight.normal};
  letter-spacing: ${primitives.typography.tracking.normal};
  flex: 1;
  min-width: 0;
`;

export const Meta = styled.div<{ $size: 'small' | 'large' }>`
  display: flex;
  align-items: center;
  gap: ${space['space-200']};
  font-family: ${bodyFont};
  font-size: ${({ $size }) =>
    $size === 'small' ? textStyle.body.md.regular.fontSize : fonts.size.base};
  line-height: ${primitives.typography.lineHeight.normal};
  letter-spacing: ${primitives.typography.tracking.normal};
  color: ${({ $size }) => ($size === 'small' ? semantic.text.primary : semantic.text.secondary)};
`;

export const PriceRow = styled.div`
  font-family: ${bodyFont};
  font-size: ${fonts.size.md};
  line-height: ${primitives.typography.lineHeight.normal};
  letter-spacing: ${primitives.typography.tracking.normal};
  color: ${semantic.text.primary};
`;

export const StatusBadge = styled(Badge)`
  align-self: flex-start;
`;

export const UnavailableOverlay = styled.div`
  position: absolute;
  z-index: 1;
  left: 50%;
  bottom: ${space['space-400']};
  transform: translateX(-50%);
  background: ${semantic.bg.secondary};
  height: ${primitives.size['size-1000']};
  padding: ${space['space-200']} ${space['space-400']};
  border-radius: ${size.radius['radius-full']};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${space['space-200']};
  max-width: calc(100% - ${space['space-400']});
  box-sizing: border-box;
  white-space: nowrap;
  box-shadow: ${shadow.sm};
`;

export const UnavailableOverlayText = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.sm};
  line-height: ${primitives.typography.lineHeight.normal};
  letter-spacing: ${primitives.typography.tracking.normal};
  color: ${semantic.text.primary};
`;

export const UnavailableOverlayDivider = styled.span`
  width: ${size.stroke['stroke-default']};
  height: ${primitives.size['size-400']};
  background: ${semantic.border.default};
  flex-shrink: 0;
`;

export const EditSearchButton = styled(TextButton)`
  && {
    font-weight: ${fonts.weight.semiBold};
    text-decoration: none;
  }
`;

export const OverlayContent = styled.div`
  position: absolute;
  z-index: 2;
  bottom: 0;
  left: 0;
  width: fit-content;
  padding: ${space['space-300']} ${space['space-400']};
  display: flex;
  flex-direction: column;
  gap: ${space['space-100']};
  color: ${semantic.text.inverse};

  & * {
    color: inherit;
  }
`;

export const OverlayTitle = styled.h3`
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  font-weight: ${fonts.weight.semiBold};
  margin: 0;
  line-height: ${primitives.typography.lineHeight.normal};
`;

export const OverlayPrice = styled.div`
  font-family: ${bodyFont};
  font-size: ${fonts.size.sm};
`;
